/**
 * Stroke icons for the landing-page cards. 24 px grid, 2 px stroke, round caps
 * and joins, no fills — one style, so a new subject gets drawn on the same
 * grid rather than borrowed from Material's filled set. Colour comes from
 * `currentColor`, so the card decides ink or amber.
 *
 * Keys match the card arrays in routes/Landing.tsx and the provider keys in
 * domain/labels.ts. An unknown provider key falls back to "other".
 */
import type { SVGProps } from "react";

const PATHS = {
  plans: (
    <>
      <polygon points="12 3 3 7.5 12 12 21 7.5 12 3" />
      <polyline points="3 16.5 12 21 21 16.5" />
      <polyline points="3 12 12 16.5 21 12" />
    </>
  ),
  subscribers: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  reports: (
    <>
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="12" y1="20" x2="12" y2="9" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </>
  ),
  billing: (
    <>
      <path d="M5 3h14v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21z" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="12" y2="16" />
    </>
  ),
  composed: (
    <>
      <circle cx="5" cy="6" r="3" />
      <circle cx="19" cy="6" r="3" />
      <circle cx="12" cy="18" r="3" />
      <path d="M6.5 8.7 10.5 15.3M17.5 8.7 13.5 15.3" />
    </>
  ),
  tooling: (
    <>
      <path d="M9 2v5M15 2v5" />
      <path d="M5 7h14v4a7 7 0 0 1-14 0z" />
      <path d="M12 18v4" />
    </>
  ),
  noprice: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </>
  ),
  explain: (
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <path d="M9 9a2 2 0 1 1 3 1.7c-.6.4-1 .8-1 1.6" />
      <line x1="11" y1="14.6" x2="11" y2="14.7" />
    </>
  ),
  network: (
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="4" cy="4" r="2" />
      <circle cx="20" cy="4" r="2" />
      <circle cx="4" cy="20" r="2" />
      <circle cx="20" cy="20" r="2" />
      <path d="M5.5 5.5l4.3 4.3M18.5 5.5l-4.3 4.3M5.5 18.5l4.3-4.3M18.5 18.5l-4.3-4.3" />
    </>
  ),
  internet: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  infrastructure: (
    <>
      <circle cx="12" cy="8" r="2" />
      <path d="M12 10v12" />
      <path d="M8 22l4-7 4 7" />
      <path d="M9.17 5.17a4 4 0 0 1 5.66 0" />
      <path d="M7.05 3.05a7 7 0 0 1 9.9 0" />
    </>
  ),
  cloud: <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />,
  hosting: (
    <>
      <rect x="2" y="3" width="20" height="7" rx="2" />
      <rect x="2" y="14" width="20" height="7" rx="2" />
      <line x1="6" y1="6.5" x2="6.01" y2="6.5" />
      <line x1="6" y1="17.5" x2="6.01" y2="17.5" />
    </>
  ),
  vpn: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      <line x1="12" y1="15" x2="12" y2="17" />
    </>
  ),
  ott: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <polygon points="10 9 15 12 10 15 10 9" />
    </>
  ),
  saas: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="M17.5 14v7M14 17.5h7" />
    </>
  ),
  other: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="8.01" y2="12" />
      <line x1="12" y1="12" x2="12.01" y2="12" />
      <line x1="16" y1="12" x2="16.01" y2="12" />
    </>
  ),
} as const;

export type FeatureIconName = keyof typeof PATHS;

export function isFeatureIconName(s: string): s is FeatureIconName {
  return s in PATHS;
}

interface Props extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: FeatureIconName;
  size?: number;
  strokeWidth?: number;
}

export function FeatureIcon({ name, size = 24, strokeWidth = 2, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
